import React, { useState } from 'react';

import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import StorageIcon from '@mui/icons-material/Storage';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import useDataEnv, { PROD_DATA_TOGGLE } from 'shared/hooks/useDataEnv';

// Only on a local dev build started with the toggle turned on (PROD_DATA_TOGGLE) — a production
// build (tools.sbly.com) never renders it, so the deployed dashboard is unaffected.
const isDev = process.env.NODE_ENV !== 'production';

/**
 * Local-only switch between the STAGING_ Snowflake clones ("Local data") and real PRODUCTION
 * data ("Production data") for the locally-running backend. Switching TO production reads AND
 * writes live prod, so it's gated behind a confirm dialog; switching back to local is immediate.
 * Self-gating: renders nothing on a production build, or locally unless the stack turned it on.
 */
export default function DataEnvToggle() {
  const [dataEnv, setDataEnv] = useDataEnv();
  const [confirming, setConfirming] = useState(false);

  if (!isDev || !PROD_DATA_TOGGLE) return null;

  const isProd = dataEnv === 'production';

  const handleClick = () => {
    if (isProd) {
      setDataEnv('local'); // dropping back to safe staging — no confirmation needed
    } else {
      setConfirming(true);
    }
  };

  return (
    <>
      <Chip
        icon={isProd ? <WarningAmberIcon /> : <StorageIcon />}
        label={isProd ? 'Production data' : 'Local data'}
        onClick={handleClick}
        size="small"
        variant="outlined"
        sx={{
          fontWeight: 600,
          letterSpacing: '0.02em',
          cursor: 'pointer',
          ...(isProd && {
            color: 'error.main',
            borderColor: 'error.light',
            backgroundColor: 'rgba(211, 47, 47, 0.08)',
            '& .MuiChip-icon': { color: 'error.main' },
            '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.14)' },
          }),
        }}
        title={
          isProd
            ? 'Local backend is hitting REAL production data — click to switch back to local (staging)'
            : 'Local backend is hitting local (staging) data — click to switch to production'
        }
      />
      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>Switch to PRODUCTION data?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your locally-running backend will read <strong>and write REAL production data</strong>.
            Any edits you make — saving a project, updating a row — are live and affect production
            immediately. Only do this if you know exactly what you're doing.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setDataEnv('production');
              setConfirming(false);
            }}
          >
            Use production data
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
